import React, { useContext, useEffect, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import classNames from 'classnames';

import { Image, Menu } from 'semantic-ui-react';

import { CONTENT_ITEM_TYPES } from '../managers/ContentManager';

import customCourseDefaultCardImage from '../img/image-custom-course-special.png';
import courseDefaultCardImage from '../img/default-course.svg';

import { SatCoreComponent, SatCoreRegister } from '../SatCoreRegistry';

import ImageService from '../services/ImageService';
import PopupService from '../services/PopupService';
import UtilityService from '../services/UtilityService';

import { stripHtmlTags } from '../utils';
import { makeAutoFocusable } from '../utils/focus';
import { makeKeyboardAccessible } from '../utils/makeKeyboardAccessible';

import '../css/CompactClassroomCourseCard.less';

const CompactClassroomCourseCard = observer((props) => {
  const {
    classroomManager, courseManager, productManager, userManager
  } = useContext(MobXProviderContext);

  const CautionIcon = SatCoreComponent('CautionIcon');
  const DotMenu = SatCoreComponent('DotMenu');

  const {
    course, disabled, handleCardClick, handleCourseCardRemove,
    handleCourseCardRename, handleReadOnlyPreview, hideTypicalCardNavButtons,
    imageUrl, openKey, showReadOnlyPreviewButton, showTrashIcon, t
  } = props;

  const [dotMenuOpen, setDotMenuOpen] = useState(false);

  // When the dot menu opens, focus the menu container and auto-close when focus leaves.
  // utilize makeKeyboardAccessible on each Menu.Item to make Tab/Enter/Space work between options.
  useEffect(() => {
    if (dotMenuOpen) {
      makeAutoFocusable(
        '.compact-classroom-course-card-dot-menu-popup.visible .menu',
        () => setDotMenuOpen(false)
      );
    }
  }, [dotMenuOpen]);

  const isCustomCourse = courseManager.isCustomCourse(course.id, course.name);
  const isLeadTeacher = classroomManager.isLeadTeacher(
    classroomManager.currentClassroomId, userManager.userId
  );

  const courseName = course?.friendlyName || course?.name;
  const courseNameObj = UtilityService.reactHtmlParserWrapper(courseName);
  const actualCourseDescription = course?.description ? stripHtmlTags(course.description) : '';

  const showLicenseExpiredCaution = !!course.licenseExpired
    && (!userManager.isStudent || productManager.allowShowExpiredLicenseWarningForStudent);

  const renderCompactClassroomCourseCard = () => {
    return (
      <div
        key={`compact_classroom_course_card_${course.id}`}
        className={classNames('CompactClassroomCourseCard', {
          'compact-classroom-course-card-disabled': disabled
        })}
        id={props.id || undefined}
        {...makeKeyboardAccessible(() => handleCardClick(course), {
          ariaLabel: t('cardAriaLabel', { courseName: stripHtmlTags(courseName) })
        })}>
        <div className='compact-classroom-course-card-inner-wrapper'>
          {renderImage()}
          {renderColorBand()}
          {renderTitleRow()}
        </div>
        {renderDotMenu()}
      </div>
    );
  };

  const renderImage = () => {
    const courseImageAltText = ImageService.getContentImageAltText({
      imageAltText: course.imageUrl ? course.altText : '',
      courseElementName: courseName,
      fallback: t('defaultCourseImageAlt')
    });
    const image = course.imageUrl ? (
      <Image alt={courseImageAltText}
        fluid
        src={CONTENT_ITEM_TYPES.getContentImageUrl(imageUrl)} />
    ) : null;

    const defaultCardImg = (isCustomCourse && courseManager.useSpecialCustomCourseCardImages)
      ? customCourseDefaultCardImage
      : courseDefaultCardImage;

    return (
      <div className='compact-card-image-wrapper'>
        <div className={classNames('compact-card-image', { default: !image })}>
          {image || (
            <Image alt={courseImageAltText} src={defaultCardImg} wrapped />
          )}
        </div>
      </div>
    );
  };

  const renderColorBand = () => {
    // TODO course.color is a legacy Ogment property with no UI to set it - fall back to default style var for now
    // TODO const colorBandStyle = course.color ? { backgroundColor: course.color } : undefined;
    const colorBandStyle = undefined;
    return (
      <div className='compact-card-color-band'
        style={colorBandStyle} />
    );
  };

  const renderTitleRow = () => {
    return (
      PopupService.renderPopup({
        className: 'compact-classroom-course-card-rollover-popup',
        content: renderFullCardDetailsRollover(),
        mouseEnterDelay: 1000,
        on: 'hover',
        position: 'top left',
        trigger: (
          <div className='compact-card-footer'>
            <div className='compact-card-title'>
              {courseNameObj.parsed}
            </div>
            {showLicenseExpiredCaution && (
              <div className='compact-card-title-caution-icon'>
                <CautionIcon expiredLicenseCautionIcon />
              </div>
            )}
          </div>
        )
      })
    );
  };

  const renderFullCardDetailsRollover = () => {
    const courseDescriptionObj = UtilityService.reactHtmlParserWrapper(
      props.description || (course.description && !props.isFromCourseShop ? course.description : null) || undefined
    );

    return (
      <div className='compact-classroom-course-card-rollover-content'
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}>
        <div className='rollover-title-wrapper'>
          <div className='rollover-title'>
            {courseNameObj.parsed}
          </div>
          {showLicenseExpiredCaution && (
            <div className='rollover-caution-icon'>
              <CautionIcon expiredLicenseCautionIcon />
            </div>
          )}
        </div>
        {(courseDescriptionObj?.parsed || actualCourseDescription) && (
          <div className='rollover-description'>
            {courseDescriptionObj.parsed}
          </div>
        )}
        {props.secondaryDescription && (
          <div className='rollover-secondary-description'>
            {props.secondaryDescription}
          </div>
        )}
      </div>
    );
  };

  const renderDotMenu = () => {
    const compactClassroomCourseMenuItems = [
      {
        key: 'menuItem_open',
        label: openKey ? t(openKey) : t('menuItem_open'),
        onClick: () => handleCardClick(course),
        shouldShow: !hideTypicalCardNavButtons
      },
      {
        key: 'menuItem_remove',
        label: t('menuItem_remove'),
        onClick: () => handleCourseCardRemove(course),
        shouldShow: !hideTypicalCardNavButtons && isLeadTeacher
      },
      {
        isDisabled: true,
        key: 'menuItem_removeDisabled',
        label: t('menuItem_removeDisabled'),
        popupContent: (
          <div className='pop-text'>
            <div>{t('courseDeletePermissionMsg')}</div>
          </div>
        ),
        popupOptions: { className: 'delete-permission-msg' },
        shouldShow: !hideTypicalCardNavButtons && !isLeadTeacher && userManager.isTeacher
      },
      {
        key: 'menuItem_rename',
        label: t('menuItem_rename'),
        onClick: () => handleCourseCardRename(course),
        shouldShow: !hideTypicalCardNavButtons && isCustomCourse
      },
      {
        key: 'menuItem_readOnlyPreview',
        label: t('menuItem_readOnlyPreview'),
        onClick: () => handleReadOnlyPreview(course),
        shouldShow: !!showReadOnlyPreviewButton
      },
      {
        key: 'menuItem_trash',
        label: t('menuItem_trash'),
        onClick: () => handleCourseCardRemove(course),
        shouldShow: isCustomCourse && hideTypicalCardNavButtons && showTrashIcon && !disabled
      }
    ];

    const availableMenuItems = compactClassroomCourseMenuItems.filter(({ shouldShow }) => shouldShow);

    if (!availableMenuItems.length) {
      return null;
    }
    return (
      <div className='compact-card-dot-menu-wrapper'
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}>
        {PopupService.renderPopup({
          className: 'compact-classroom-course-card-dot-menu-popup',
          content: <Menu vertical>{availableMenuItems.map(renderMenuItem)}</Menu>,
          on: 'click',
          onClose: () => setDotMenuOpen(false),
          onOpen: () => setDotMenuOpen(true),
          open: dotMenuOpen,
          position: 'bottom right',
          trigger: (
            <DotMenu
              ariaLabel={t('courseCardMenuAriaLabel')}
              clickMenu={() => setDotMenuOpen((prev) => !prev)} />
          ),
          wide: false
        })}
      </div>
    );
  };

  const renderMenuItem = ({
    key,
    label,
    onClick,
    isDisabled,
    className,
    popupContent,
    popupOptions
  } = {}) => {
    const menuItem = (
      <Menu.Item
        className={classNames(className || key, { disabled: isDisabled })}
        name={key}
        {...makeKeyboardAccessible(onClick || (() => {}), {
          disabled: isDisabled,
          role: 'menuitem'
        })}>
        {label}
      </Menu.Item>
    );

    if (!popupContent) {
      return React.cloneElement(menuItem, { key });
    }

    return (
      <React.Fragment key={key}>
        {PopupService.renderPopup({
          content: popupContent,
          on: 'hover',
          position: 'bottom center',
          ...popupOptions,
          trigger: menuItem
        })}
      </React.Fragment>
    );
  };

  return renderCompactClassroomCourseCard();
});

export default CompactClassroomCourseCard;
SatCoreRegister('CompactClassroomCourseCard', CompactClassroomCourseCard);
